import { create } from 'zustand';
import { AssessmentResult } from '../types/assessment';
import { API_URL } from '../utils/api';

import { User } from 'firebase/auth';

export type Role = 'user' | 'contributor' | 'admin';

export interface AppUser {
  uid: string;
  email: string | null;
  display_name: string | null;
  photo_url: string | null;
  role: Role;
  notes: string | null;
  created_at: number;
  last_login_at: number;
}

/** A single streamed-progress entry captured during an assessment. Admin-only. */
export interface ProgressEvent {
  t: number;          // seconds since the assessment started
  message: string;
  kind: string;       // 'progress' | 'outcome' | 'result' | 'error'
}

export interface AssessmentHistoryItem {
  id: string;
  source_id: string;
  timestamp: number;
  url: string | null;
  input_text: string | null;
  inputType: 'url' | 'text';
  provider: 'claude' | 'gemini';
  scores: {
    final: number;
    rating: string;
  };
  result: AssessmentResult;
  feedback?: string;
  feedback_timestamp?: number;
  progress_events?: ProgressEvent[];  // present only for admins
}

interface AnalysisStore {
  // Auth state
  firebaseUser: User | null;
  firebaseToken: string | null;
  currentUser: AppUser | null;

  // v2.5 HYBRID assessment state
  currentAssessment: AssessmentResult | null;
  assessmentLoading: boolean;
  assessmentProgress: string;
  assessmentProgressLog: string[];
  assessmentInputUrl: string | null;
  assessmentHistory: AssessmentHistoryItem[];
  sourceHistory: AssessmentHistoryItem[];
  error: string | null;

  // Actions
  setAuth: (user: User | null, token: string | null) => void;
  fetchCurrentUser: () => Promise<void>;

  // v2.5 HYBRID assessment actions
  setAssessment: (result: AssessmentResult | null, input?: string, inputType?: 'url' | 'text', provider?: 'claude' | 'gemini') => void;
  setAssessmentLoading: (loading: boolean, url?: string | null) => void;
  setAssessmentProgress: (message: string) => void;
  setCurrentAssessmentFromHistory: (item: AssessmentHistoryItem) => void;
  removeAssessment: (id: string) => Promise<void>;
  removeSourceAssessments: (sourceId: string) => Promise<void>;
  submitFeedback: (id: string, feedback: string) => Promise<AssessmentHistoryItem | null>;
  clearAssessmentHistory: () => void;
  resetAssessment: () => void;
  setError: (error: string | null) => void;
  fetchGlobalHistory: () => Promise<void>;
  fetchSourceHistory: (sourceId: string) => Promise<void>;
  fetchRecordById: (id: string) => Promise<AssessmentHistoryItem | null>;
}

// localStorage helpers
const ASSESS_KEY = 'assessment-history';

const loadAssessmentHistory = (): AssessmentHistoryItem[] => {
  try {
    const stored = localStorage.getItem(ASSESS_KEY);
    if (!stored) return [];
    
    const items = JSON.parse(stored);
    if (!Array.isArray(items)) return [];

    // v2.5 Migration: Filter out legacy records that don't have the new required structure
    return items.filter(item => {
      const isValid = 
        item && 
        typeof item.id === 'string' && 
        typeof item.source_id === 'string' &&
        item.result &&
        item.result.scores &&
        typeof item.result.scores.final === 'number';
      
      if (!isValid) {
        console.warn('[AnalysisStore] Dropping legacy/invalid history item:', item?.id || 'unknown');
      }
      return isValid;
    });
  } catch (error) {
    console.error('Failed to load assessment history:', error);
  }
  return [];
};

const saveAssessmentHistory = (items: AssessmentHistoryItem[]) => {
  try {
    localStorage.setItem(ASSESS_KEY, JSON.stringify(items));
  } catch (error) {
    console.error('Failed to save assessment history:', error);
  }
};

const initialAssessmentHistory = loadAssessmentHistory();

export const useAnalysisStore = create<AnalysisStore>()((set, get) => ({
  // Auth state
  firebaseUser: null,
  firebaseToken: null,
  currentUser: null,

  // Assessment state
  currentAssessment: null,
  assessmentLoading: false,
  assessmentProgress: '',
  assessmentProgressLog: [],
  assessmentInputUrl: null,
  assessmentHistory: initialAssessmentHistory,
  sourceHistory: [],
  error: null,

  // ─── Actions ───
  setAuth: (user, token) => {
    // When the user signs out (or hasn't signed in beyond anonymous), drop any
    // cached history and the cached user record so the previous user's data
    // is not visible.
    const isAuthenticated = !!user && !user.isAnonymous;
    set((state) => ({
      firebaseUser: user,
      firebaseToken: token,
      currentUser: isAuthenticated ? state.currentUser : null,
      assessmentHistory: isAuthenticated ? state.assessmentHistory : [],
      sourceHistory: isAuthenticated ? state.sourceHistory : [],
    }));
    if (!isAuthenticated) {
      saveAssessmentHistory([]);
    }
  },

  fetchCurrentUser: async () => {
    const token = get().firebaseToken;
    if (!token) return;
    try {
      const response = await fetch(`${API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        set({ currentUser: null });
        return;
      }
      const user = await response.json();
      set({ currentUser: user as AppUser });
    } catch (err) {
      console.error('Failed to fetch current user:', err);
      set({ currentUser: null });
    }
  },

  // ─── Assessment actions ───
  setAssessment: (result, input, inputType, provider) => {
    if (result && input) {
      // Add to assessment history
      const item: AssessmentHistoryItem = {
        id: result.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        source_id: result.source_id || 'manual-text',
        timestamp: Date.now(),
        url: inputType === 'url' ? input : null,
        input_text: inputType === 'text' ? input : null,
        inputType: inputType || 'url',
        provider: provider || 'claude',
        scores: {
          final: result.scores.final,
          rating: result.rating
        },
        result,
      };
      set((state) => {
        const newHistory = [item, ...state.assessmentHistory].slice(0, 100);
        saveAssessmentHistory(newHistory);
        return {
          currentAssessment: result,
          assessmentLoading: false,
          assessmentProgress: '',
          assessmentProgressLog: [],
          assessmentHistory: newHistory,
          // Also update sourceHistory if it matches the new item's source
          sourceHistory: item.source_id !== 'manual-text' 
            ? [item, ...state.sourceHistory].filter(i => i.source_id === item.source_id)
            : state.sourceHistory
        };
      });
    } else {
      set({ currentAssessment: result, assessmentLoading: false, assessmentProgress: '', assessmentProgressLog: [] });
    }
  },

  setAssessmentLoading: (loading, url = null) => {
    set({
      assessmentLoading: loading,
      error: loading ? null : get().error,
      currentAssessment: loading ? null : get().currentAssessment,
      assessmentProgress: loading ? 'Starting assessment...' : '',
      assessmentProgressLog: loading ? ['Starting assessment...'] : [],
      assessmentInputUrl: loading ? url : null,
    });
  },

  setAssessmentProgress: (message) => {
    set((state) => {
      const log = state.assessmentProgressLog;
      // Append each distinct step; ignore repeats of the latest message so the
      // checklist reads as a sequence of stages rather than duplicate lines.
      const nextLog = log[log.length - 1] === message ? log : [...log, message];
      return { assessmentProgress: message, assessmentProgressLog: nextLog };
    });
  },

  setCurrentAssessmentFromHistory: (item) => {
    set({ 
      currentAssessment: {
        ...item.result,
        id: item.result?.id || item.id,
        source_id: item.result?.source_id || item.source_id,
      } as AssessmentResult
    });
  },

  removeAssessment: async (id) => {
    try {
      const fullUrl = `${API_URL}/assessments?id=${id}`;
      const token = get().firebaseToken;
      
      const response = await fetch(fullUrl, { 
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
      });

      if (!response.ok) {
        throw new Error(`Delete request failed with status ${response.status}`);
      }
    } catch (err) {
      console.error('Failed to delete assessment from server:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to delete assessment from server' });
      return; // Do not clear local state if server deletion failed
    }

    set((state) => {
      const newHistory = state.assessmentHistory.filter((a) => a.id !== id);
      const newSourceHistory = state.sourceHistory.filter((a) => a.id !== id);
      saveAssessmentHistory(newHistory);
      return {
        assessmentHistory: newHistory,
        sourceHistory: newSourceHistory,
        currentAssessment: state.assessmentHistory.find(a => a.id === id)?.result === state.currentAssessment
          ? null
          : state.currentAssessment,
      };
    });
  },

  removeSourceAssessments: async (sourceId) => {
    try {
      const fullUrl = `${API_URL}/assessments?source_id=${sourceId}`;
      const token = get().firebaseToken;
      
      const response = await fetch(fullUrl, { 
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
      });

      if (!response.ok) {
        throw new Error(`Bulk delete request failed with status ${response.status}`);
      }
    } catch (err) {
      console.error('Failed to bulk delete assessments from server:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to bulk delete assessments from server' });
      return; // Do not clear local state if server deletion failed
    }

    set((state) => {
      const newHistory = state.assessmentHistory.filter((a) => a.source_id !== sourceId);
      saveAssessmentHistory(newHistory);
      return {
        assessmentHistory: newHistory,
        sourceHistory: [],
        currentAssessment: state.currentAssessment?.source_id === sourceId ? null : state.currentAssessment,
      };
    });
  },

  clearAssessmentHistory: () => {
    set({ assessmentHistory: [], sourceHistory: [], currentAssessment: null });
    saveAssessmentHistory([]);
  },

  resetAssessment: () => {
    set({
      currentAssessment: null,
      assessmentLoading: false,
      assessmentProgress: '',
      assessmentProgressLog: [],
      assessmentInputUrl: null,
      error: null,
    });
  },

  setError: (error) => {
    set({ error });
  },

  fetchGlobalHistory: async () => {
    try {
      const fullUrl = `${API_URL}/assessments`;
      const token = get().firebaseToken;
      
      const response = await fetch(fullUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
           // If unauthorized, clear history
           set({ assessmentHistory: [] });
           saveAssessmentHistory([]);
           return;
        }
        throw new Error('Failed to fetch assessments');
      }
      const data = await response.json();
      
      set({ assessmentHistory: data.assessments || [] });
      saveAssessmentHistory(data.assessments || []);
    } catch (err) {
      console.error('Failed to load global history:', err);
    }
  },

  fetchSourceHistory: async (sourceId) => {
    try {
      const fullUrl = `${API_URL}/assessments?source_id=${sourceId}`;
      const token = get().firebaseToken;
      
      const response = await fetch(fullUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
      });
      if (!response.ok) throw new Error('Failed to fetch source assessments');
      const data = await response.json();
      
      set({ sourceHistory: data.assessments || [] });
    } catch (err) {
      console.error('Failed to load source history:', err);
    }
  },

  submitFeedback: async (id, feedback) => {
    try {
      const fullUrl = `${API_URL}/assessments/${id}/feedback`;
      const token = get().firebaseToken;

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ feedback }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || `Feedback submission failed (${response.status})`);
      }

      const data = await response.json();
      const updated: AssessmentHistoryItem | undefined = data.assessment;
      const feedbackTimestamp = updated?.feedback_timestamp ?? Date.now();

      set((state) => {
        const newHistory = state.assessmentHistory.map((item) =>
          item.id === id ? { ...item, feedback, feedback_timestamp: feedbackTimestamp } : item
        );
        const newSourceHistory = state.sourceHistory.map((item) =>
          item.id === id ? { ...item, feedback, feedback_timestamp: feedbackTimestamp } : item
        );
        saveAssessmentHistory(newHistory);
        return {
          assessmentHistory: newHistory,
          sourceHistory: newSourceHistory,
        };
      });

      return get().assessmentHistory.find((item) => item.id === id) || null;
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to submit feedback' });
      return null;
    }
  },

  fetchRecordById: async (id) => {
    try {
      const fullUrl = `${API_URL}/assessments?id=${id}`;
      const token = get().firebaseToken;
      const response = await fetch(fullUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
      });
      if (!response.ok) throw new Error('Failed to fetch assessment');
      const data = await response.json();
      
      if (data.assessments && data.assessments.length > 0) {
        return data.assessments[0];
      }
      return null;
    } catch (err) {
      console.error('Failed to fetch record by ID:', err);
      return null;
    }
  },
}));
